import styled from 'styled-components';

// Container matching the HTML structure
export const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  min-height: 100vh;
  background: transparent;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
    max-width: 100%;
    width: 100%;
    overflow-x: hidden;
  }
`;

// Top Row Layout - Search/Filters on left, Quick Actions on right
export const TopRowLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: stretch;
  width: 100%;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

export const SearchFiltersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const QuickActionsWrapper = styled.div`
  display: flex;
  align-items: stretch;
  min-width: 200px;

  & > div {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 1024px) {
    min-width: auto;
  }

  @media (max-width: 768px) {
    width: 100%;

    & > div {
      width: 100%;
    }
  }
`;

// Full Width Table Section
export const TableSection = styled.div`
  width: 100%;
`;

// Legacy exports for backwards compatibility (can be removed later)
export const DashboardLayout = styled.div`
  display: grid;
  grid-template-columns: 3.1fr 1fr;
  gap: 24px;
  align-items: start;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    display: block;
    width: 100%;
  }
`;

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const Sidebar = styled.div`
  position: sticky;
  top: 20px;

  @media (max-width: 768px) {
    position: static;
    width: 100%;
    margin-top: 16px;
  }
`;

// Header Container - matches HTML structure
export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    align-items: center;
    gap: 12px;
  }
`;

// Icon Wrapper with orange background
export const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(250, 70, 22, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    border-radius: 8px;

    svg {
      width: 20px !important;
      height: 20px !important;
    }
  }
`;

export const HeaderContent = styled.div`
  flex: 1;
`;

export const Title = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.primary
      : props.theme.colors.blueBlack};
  letter-spacing: -0.5px;
  margin: 0;
  line-height: 1.2;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;

  @media (max-width: 768px) {
    font-size: 22px;
    line-height: 28px;
    margin: 0;
  }
`;

// Metrics Grid
export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 30px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    overflow-y: visible;
    gap: 10px;
    /* Extend to edges - negative margin cancels container padding, padding adds it back for content */
    margin: 0 -16px 16px -16px;
    padding: 0 16px 8px 16px;
    width: calc(100% + 16px);
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

// Metric Card clean design
export const MetricCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};
  border-left: 4px solid ${(props) => props.theme?.colors?.primary || '#FA4616'};
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 100px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px
      rgba(
        0,
        0,
        0,
        ${(props) => (props.theme.name === 'dark' ? '0.3' : '0.15')}
      );
  }

  @media (max-width: 768px) {
    min-width: 140px;
    padding: 20px 12px;
    flex-shrink: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    min-height: auto;
    height: auto;
  }
`;

export const MetricContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  gap: 2px;
`;

export const MetricValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.primary
      : props.theme.colors.blueBlack};
  margin: 4px 0;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 24px;
    margin: 2px 0;
  }
`;

export const MetricLabel = styled.div`
  font-size: 13px;
  letter-spacing: 0.03em;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.muted};
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: visible;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 12px;
    letter-spacing: 0.02em;
    line-height: 1.2;
    margin-bottom: 2px;
  }
`;

export const MetricIcon = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  width: 36px;
  height: 100%;
  align-self: stretch;
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 768px) {
    width: 32px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const MetricChange = styled.div`
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  color: ${(props) =>
    props.positive ? props.theme?.colors?.success || '#22C55E' : '#ef4444'};
  font-weight: 500;
  margin-top: 4px;

  svg {
    width: 10px;
    height: 10px;
  }

  @media (max-width: 768px) {
    font-size: 11px;
    margin-top: 2px;
    gap: 2px;

    svg {
      width: 10px;
      height: 10px;
    }
  }
`;
